import { DateTime } from 'luxon';

import { MAX_DATE, MIN_DATE } from '@/constants/dates';

/**
 * Determines if a date is within a given range.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string|Date|DateTime} [params.date=DateTime.now().setZone('utc')] - The date to check. Defaults to the current UTC date.
 * @param {string|Date|DateTime} [params.startDate=DateTime.fromJSDate(MIN_DATE).setZone('utc')] - The start date of the range. Defaults to the minimum date.
 * @param {string|Date|DateTime} [params.endDate=DateTime.fromJSDate(MAX_DATE).setZone('utc')] - The end date of the range. Defaults to the maximum date.
 * @param {boolean} [params.isStartDateInclusive=true] - Whether the start date is inclusive. Defaults to `true`.
 * @param {boolean} [params.isEndDateInclusive=true] - Whether the end date is inclusive. Defaults to `true`.
 * @returns {boolean} `true` if the date is within the range, `false` otherwise.
 *
 * @example
 * // All parameters as ISO strings
 * isDateInRange({ date: '2024-01-01', startDate: '2024-01-01', endDate: '2024-01-01' }); // true
 *
 * @example
 * // Date as a JavaScript Date object, range as ISO strings
 * isDateInRange({ date: new Date('2024-06-15'), startDate: '2024-06-01', endDate: '2024-06-30' }); // true
 *
 * @example
 * // Date before the range
 * isDateInRange({ date: '2024-05-31', startDate: '2024-06-01', endDate: '2024-06-30' }); // false
 *
 * @example
 * // Omitting parameters: checks if now is in the full possible range
 * isDateInRange({}); // true
 *
 * @example
 * // Omitting startDate: checks if date is before or on the endDate
 * isDateInRange({ date: '2024-01-01', endDate: '2024-12-31' }); // true
 *
 * @example
 * // Omitting endDate: checks if date is after or on the startDate
 * isDateInRange({ date: '2024-12-31', startDate: '2024-01-01' }); // true
 *
 * @example
 * // Omitting date: checks if now is in the given range
 * isDateInRange({ startDate: '2024-01-01', endDate: '2024-12-31' });
 *
 * @example
 * // Controlling inclusivity of range boundaries
 * isDateInRange({
 *   date: '2024-06-01',
 *   startDate: '2024-06-01',
 *   endDate: '2024-06-30',
 *   isStartDateInclusive: false,
 *   isEndDateInclusive: true,
 * }); // false (exclusive start)
 */
const isDateInRange = ({
  date = DateTime.now().setZone('utc'),
  startDate = DateTime.fromJSDate(MIN_DATE).setZone('utc'),
  endDate = DateTime.fromJSDate(MAX_DATE).setZone('utc'),
  isStartDateInclusive = true,
  isEndDateInclusive = true,
}) => {
  try {
    let formattedDate;
    let formattedStartDate;
    let formattedEndDate;

    // Convert dates to luxon DateTime objects from ISO strings or JS date objects

    if (typeof date === 'string') {
      formattedDate = DateTime.fromISO(date);
    } else if (date instanceof Date) {
      formattedDate = DateTime.fromJSDate(date);
    } else if (DateTime.isDateTime(date)) {
      formattedDate = date;
    }

    if (typeof startDate === 'string') {
      formattedStartDate = DateTime.fromISO(startDate);
    } else if (startDate instanceof Date) {
      formattedStartDate = DateTime.fromJSDate(startDate);
    } else if (DateTime.isDateTime(startDate)) {
      formattedStartDate = startDate;
    }

    if (typeof endDate === 'string') {
      formattedEndDate = DateTime.fromISO(endDate);
    } else if (endDate instanceof Date) {
      formattedEndDate = DateTime.fromJSDate(endDate);
    } else if (DateTime.isDateTime(endDate)) {
      formattedEndDate = endDate;
    }

    // Handle invalid dates
    if (
      !formattedDate.isValid ||
      !formattedStartDate.isValid ||
      !formattedEndDate.isValid
    ) {
      throw new Error('Invalid date(s)', { date, startDate, endDate });
    }

    // Check if the date is within the range with optional inclusivity
    const isAfterStart = isStartDateInclusive
      ? formattedStartDate <= formattedDate
      : formattedStartDate < formattedDate;

    const isBeforeEnd = isEndDateInclusive
      ? formattedDate <= formattedEndDate
      : formattedDate < formattedEndDate;

    return isAfterStart && isBeforeEnd;
  } catch (error) {
    return false;
  }
};

export default isDateInRange;
